import { Formik } from 'formik';
import React, { useReducer } from 'react';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import {
  getEnabledPaymentProviders,
  isBuyNowPayLater,
} from 'Components/widgets/payment-info/payment-form/paymentProvidersUtils';
import { PaymentForm } from 'Components/widgets/payment-info/PaymentForm';
import { partition } from 'lodash';
import { B2BPaymentViewProvider } from './b2b-payment-info/B2BPaymentViewContext';
import { persistPaymentMethodInCookies } from './PaymentInfo.utils';
export type PayingHousehold = {
  paymentSchedule: PaymentSchedule;
  customerId: string;
};
type Props = {
  className?: string;
  totalPrice?: Price | null | undefined;
  i18n: I18N;
  paymentProviders: ReadonlyArray<PaymentProvider>;
  paymentSchedule: PaymentSchedule;
  deadlineDate?: number | null | undefined;
  price: Price;
  payingHousehold: PayingHousehold;
  withB2B?: boolean;
};
const defaultState = {
  shouldShowPaymentProcess: false,
  paymentProcessOnError: false,
  paymentProcessId: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'showPaymentProcess':
      return {
        shouldShowPaymentProcess: true,
        paymentProcessOnError: false,
        paymentProcessId: Date.now(), // just for trigger the re-fetch of data in payment process
      };

    case 'paymentProcessError':
      return { ...state, paymentProcessOnError: true };

    case 'reset':
      return defaultState;

    default:
      throw new Error();
  }
};

export const PaymentInfo = ({
  className,
  i18n,
  paymentProviders,
  paymentSchedule,
  totalPrice,
  deadlineDate,
  price,
  payingHousehold,
}: Props) => {
  const { isTablet } = useResponsive();
  const appContext = useAppContext();
  const [{ shouldShowPaymentProcess, paymentProcessId, paymentProcessOnError }, dispatch] =
    useReducer(reducer, defaultState);
  const enabledPaymentProviders = getEnabledPaymentProviders(
    paymentProviders,
    !isTablet,
    appContext,
  );
  const [availablePaymentProviders, buyNowPayLaterProviders] = partition(
    enabledPaymentProviders,
    (provider) => !isBuyNowPayLater(provider),
  );
  const shouldPrioritizeFullPayment = isFeatureFlipped(appContext)('booking.prioritizeFullPayment');
  const isBillingAddressMandatory = availablePaymentProviders.some(
    ({ withBillingAddress }) => withBillingAddress,
  );
  const amountToPay = shouldPrioritizeFullPayment
    ? payingHousehold.paymentSchedule.total.value
    : payingHousehold.paymentSchedule.depositAmount?.value ||
      payingHousehold.paymentSchedule.total.value;
  const initialValues = {
    freeDeposit: appContext.isSeller ? amountToPay : '',
    amountToPay,
    paymentProviderId: availablePaymentProviders?.[0]?.id,
    ...(isBillingAddressMandatory && {
      billingDetails: {
        countryCode: appContext.country,
      },
    }),
    selectorPaymentProviderId: availablePaymentProviders?.[0]?.selectorId,
    paymentMethodId: '',
    paymentConditionId: '',
    termsAndConditions: {
      cgv: false,
      donation: false,
    },
    HealthRegulations: {
      cgv: false,
    },
    currentPayingCustomerId: payingHousehold.customerId,
  };
  return (
    <SiteContainer className={className} size="small">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(
          {
            paymentProduct,
            paymentProviderId,
            paymentMethodId,
          }: {
            paymentProduct?: string;
            paymentProviderId: string;
            paymentMethodId: string;
          },
          actions,
        ) => {
          persistPaymentMethodInCookies({
            paymentProduct,
            paymentMethodId,
            paymentProvider: enabledPaymentProviders.find(({ id }) => id === paymentProviderId),
          });
          dispatch({
            type: 'showPaymentProcess',
          });
          actions.setStatus({
            paymentProcessOnError: false,
          });
        }}
        validateOnMount
        validateOnBlur={false}
      >
        <div>
          <B2BPaymentViewProvider>
            <PaymentForm
              i18n={i18n}
              shouldShowPaymentProcess={shouldShowPaymentProcess}
              paymentProcessId={paymentProcessId}
              paymentProcessOnError={paymentProcessOnError}
              paymentProviders={availablePaymentProviders}
              totalPrice={totalPrice}
              buyNowPayLaterProviders={buyNowPayLaterProviders}
              paymentSchedule={paymentSchedule}
              price={price}
              deadlineDate={deadlineDate}
              dispatch={dispatch}
            />
          </B2BPaymentViewProvider>
        </div>
      </Formik>
    </SiteContainer>
  );
};
